/**
 * Regra da descrição de atuação por faixa de percentual, no cadastro da Realização Destaque.
 *
 * 25% e 50% têm enunciado fechado no regulamento — a área dispensou o autor de descrever essas
 * faixas, então o campo nasce vazio e o cadastro avança sem ele. 75% e 100% representam
 * protagonismo e continuam exigindo redação própria.
 *
 * O pré-preenchimento automático de 25%/50% que existiu aqui foi revertido a pedido da área
 * (homologação 2S2026): o campo vinha com o texto do regulamento escrito, e a decisão foi
 * deixá-lo em branco e dispensado.
 */

/** Faixas em que o autor é obrigado a descrever a atuação com as próprias palavras. */
export const PERCENTS_REQUIRING_OWN_DESCRIPTION = [75, 100];

/** Faixas em que descrever a atuação é dispensado — campo vazio não bloqueia o avanço. */
export const PERCENTS_DISPENSING_DESCRIPTION = [25, 50];

/** `true` para as faixas que exigem redação própria (campo vazio + aviso). */
export function requiresOwnDescription(percent: unknown): boolean {
  return PERCENTS_REQUIRING_OWN_DESCRIPTION.includes(Number(percent));
}

/** `true` para as faixas em que a descrição é opcional. */
export function dispensesDescription(percent: unknown): boolean {
  return PERCENTS_DISPENSING_DESCRIPTION.includes(Number(percent));
}

export type MessageLength = { textLength: number; fullLength: number };

/**
 * Tamanho de uma descrição já gravada, para quando o editor de texto ainda não informou o tamanho.
 *
 * O editor só informa o tamanho quando o conteúdo muda. Na edição e no rascunho restaurado a tela
 * recarrega a equipe depois disso, e o tamanho informado se perde. A conta é a mesma do editor:
 * marcação fora, quebra de linha e `&nbsp;` como espaço, as demais entidades como um caractere,
 * espaços repetidos como um, pontas aparadas.
 */
export function measureStoredMessage(message: unknown): MessageLength {
  if (typeof message !== 'string') return { textLength: 0, fullLength: 0 };

  const text = message
    .replace(/<br\s*\/?>|<\/(p|div|li|h[1-6]|blockquote)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/gi, '_')
    .replace(/\s+/g, ' ')
    .trim();

  return { textLength: text.length, fullLength: text.length };
}

export type ParticipationMessageValidation = {
  valid: boolean;
  hasFieldsEmpty: boolean;
  enteredMinimum: boolean;
  exceededMaximum: boolean;
};

/**
 * Valida as descrições de atuação da etapa 0.
 *
 * Saiu do componente para cá porque a regra deixou de ser "todo mundo preenche": agora ela
 * depende do percentual de cada participante, e um `if` a mais dentro de um `useMemo` de um
 * arquivo de 2.500 linhas seria impossível de exercitar sem montar a tela inteira.
 *
 * Vazio só passa quando a faixa dispensa (25% e 50%). O que foi escrito — em qualquer faixa —
 * continua sujeito à régua de 60–200 caracteres: dispensar a descrição não é permitir descrição
 * pela metade.
 */
export function validateParticipationMessages(
  target: any[] | undefined,
  teamDistribution: Record<string, any> | undefined,
  limits: { min: number; max: number },
): ParticipationMessageValidation {
  if (!target?.length) {
    return { valid: false, hasFieldsEmpty: true, enteredMinimum: false, exceededMaximum: false };
  }

  const result: ParticipationMessageValidation = {
    valid: true,
    hasFieldsEmpty: false,
    enteredMinimum: true,
    exceededMaximum: false,
  };

  for (const teamItem of target) {
    const { message, messageLength: informedLength } = teamItem ?? {};
    const hasStoredText = typeof message === 'string' && message.trim().length > 0;
    // O tamanho informado pelo editor vale sempre que existe, mesmo zerado.
    const messageLength: MessageLength | undefined = informedLength ?? (hasStoredText ? measureStoredMessage(message) : undefined);

    const isEmpty =
      typeof message !== 'string' || !message || !message.trim().length || !messageLength || messageLength.textLength <= 0;

    if (isEmpty) {
      if (dispensesDescription(teamDistribution?.[teamItem?._id])) continue;

      result.valid = false;
      result.hasFieldsEmpty = true;
    }

    if (messageLength && (messageLength.textLength < limits.min || messageLength.fullLength > limits.max)) {
      result.valid = false;
      result.enteredMinimum = messageLength.textLength >= limits.min;
      result.exceededMaximum = messageLength.fullLength > limits.max;
    }
  }

  return result;
}
